import type { TimeZoneMode } from '@/lib/format';

/**
 * The report range is held as UTC INSTANTS, never as display strings.
 *
 * That is what makes the console's UTC/local toggle actually work here: flipping
 * it re-renders the same instant in the other zone instead of silently
 * reinterpreting the typed digits as a different moment. The query is always
 * sent in UTC regardless of what the user is looking at.
 *
 * Everything is floored to the hour, because the rollup is hourly — offering
 * minutes would imply a precision the data does not have.
 */

const pad = (n: number): string => String(n).padStart(2, '0');

/** UTC instant → `YYYY-MM-DDTHH:00` rendered in the chosen zone. */
export function toHourInput(ms: number, tz: TimeZoneMode): string {
  const d = new Date(ms);
  return tz === 'utc'
    ? `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:00`
    : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:00`;
}

/** `YYYY-MM-DDTHH:mm` typed in the chosen zone → UTC instant, floored to the hour. */
export function fromHourInput(value: string, tz: TimeZoneMode): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2})/.exec(value);
  if (!m) return null;
  const [, y, mo, da, h] = m;
  return tz === 'utc'
    ? Date.UTC(Number(y), Number(mo) - 1, Number(da), Number(h))
    : new Date(Number(y), Number(mo) - 1, Number(da), Number(h)).getTime();
}

/** UTC instant → the `YYYY-MM-DD HH:00:00` the ClickHouse query expects. */
export function toClickhouseUtc(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:00:00`;
}

/** Floor an instant to the top of its hour. */
export function floorToHour(ms: number): number {
  return Math.floor(ms / 3_600_000) * 3_600_000;
}

/** Default window: start of the current UTC month → the current hour. */
export function defaultRange(): { from: number; to: number } {
  const now = new Date();
  const from = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
  return { from, to: floorToHour(now.getTime()) + 3_600_000 };
}
